/**
 * RebaselineNotice: a planned series rebaseline, shown wherever a series or a
 * run on a rebaselined segment is read (backend app/services/series_rebaseline.py).
 * It gives the date, the battery version, the note and the full change list.
 * It renders nothing when given no rebaseline, so a series never rebaselined
 * looks exactly as before.
 *
 *   RebaselineNotice  - the event itself (banner); `newBaseline` adds the
 *                       "this read is the new baseline" sentence.
 *   NewBaselineChip   - compact label for run lists and pickers.
 */
import { GitCommitVertical } from "lucide-react";

import {
  formatRebaselineDate,
  type RunSeries,
  type SeriesRebaseline,
} from "@/api/series-rebaseline";

const tone = {
  color: "var(--foreground)",
  background: "var(--spectral-violet-5)",
  border: "1px solid var(--spectral-violet-30)",
} as const;

export function rebaselineHeadline(rebaseline: SeriesRebaseline): string {
  const date = formatRebaselineDate(rebaseline.applied_at);
  return `Series rebaselined${date ? ` on ${date}` : ""} · battery v${rebaseline.battery_version ?? "?"}`;
}

export function RebaselineNotice({
  rebaseline,
  newBaseline = false,
  title,
  compact = false,
}: {
  rebaseline: SeriesRebaseline | null | undefined;
  /** The run being shown is this rebaseline's new baseline. */
  newBaseline?: boolean;
  /** Overrides the headline (e.g. "Compared across a rebaseline"). */
  title?: string;
  /** Collapse the change list behind a disclosure. */
  compact?: boolean;
}) {
  if (!rebaseline) return null;
  const lines = rebaseline.lines ?? [];
  const list = lines.length > 0 && (
    <ul style={{ margin: "4px 0 0 18px", listStyle: "disc", overflowWrap: "anywhere" }}>
      {lines.map((line) => <li key={line}>{line}</li>)}
    </ul>
  );
  return (
    <div
      role="note"
      style={{ ...tone, marginTop: 8, padding: "8px 12px", borderRadius: 8, fontSize: 12, lineHeight: 1.5 }}
    >
      <div className="flex items-center gap-1.5" style={{ fontWeight: 600 }}>
        <GitCommitVertical className="w-3.5 h-3.5 shrink-0" style={{ color: "var(--spectral-violet)" }} />
        {title ?? rebaselineHeadline(rebaseline)}
      </div>
      {newBaseline && (
        <div style={{ marginTop: 2 }}>
          This read is the series' new baseline. Reads before it were measured differently;
          comparisons across the marker say so.
        </div>
      )}
      {rebaseline.note && (
        <div style={{ marginTop: 2, color: "var(--muted-foreground)" }}>{rebaseline.note}</div>
      )}
      {list && (compact ? (
        <details style={{ marginTop: 2 }}>
          <summary style={{ cursor: "pointer", color: "var(--spectral-violet)" }}>
            What changed ({lines.length})
          </summary>
          {list}
        </details>
      ) : list)}
    </div>
  );
}

export function NewBaselineChip({ series }: { series: RunSeries | null | undefined }) {
  if (!series || series.segment === 0) return null;
  return (
    <span
      title={series.new_baseline
        ? `New baseline after a series rebaseline (battery v${series.battery_version})`
        : `Measured on battery v${series.battery_version}, after a series rebaseline`}
      className="inline-flex items-center gap-1"
      style={{
        ...tone, fontSize: 11, fontWeight: 600, padding: "1px 8px", borderRadius: 999,
        whiteSpace: "nowrap",
      }}
    >
      <GitCommitVertical className="w-3 h-3" style={{ color: "var(--spectral-violet)" }} />
      {series.new_baseline ? "New baseline" : "Battery"} · v{series.battery_version}
    </span>
  );
}
